import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Stack,
  Box,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ButtonOne from "../../../common/normal-button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { ColorsConstants } from "../../../cssUtils/constants";
import { ReactComponent as UploadMain } from "../../../assets/images/icons/campaign/broadcast/upload-main.svg";
import typography from "../../../cssUtils/typography";

// A CSV needs a header row plus at least one row that has something in it
export const EMPTY_CSV_ERROR = "The CSV file has no records. Add at least one row below the header and try again.";
export const csvHasDataRows = (text) =>
  text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .slice(1)
    .some((line) => line.replace(/[,;\s"]/g, "") !== "");

const UploadCSV = forwardRef(function UploadCSV(props, ref) {
  const {
    csvFile,
    setCsvFile,
    campaignsData,
    isEditMode
  } = props;

  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  /**
   * Expose imperative API to parent
   */
  useImperativeHandle(ref, () => ({
    resetCsv() {
      setCsvFile(null);
      setError(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
  }));

  // Sync with parent state when coming back to this step
  useEffect(() => {
    if (csvFile) {
      setError(null);
    }
  }, [csvFile]);

  const sampleFileURL = "/sample.csv";

const handleFileChange = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  if (!file.name.toLowerCase().endsWith(".csv")) {
    setError("Please upload a CSV file");
    setCsvFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    return;
  }

  try {
    const arrayBuffer = await file.arrayBuffer();

    // Try Windows-1252 first (Excel-generated CSVs)
    let content;

    try {
      content = new TextDecoder("windows-1252").decode(arrayBuffer);
    } catch (e) {
      content = new TextDecoder("utf-8").decode(arrayBuffer);
    }

    // Remove problematic characters
    content = content
      .replace(/\u2013/g, "-") // en dash
      .replace(/\u2014/g, "-") // em dash
      .replace(/\u2018|\u2019/g, "'") // smart quotes
      .replace(/\u201C|\u201D/g, '"') // smart double quotes
      .replace(/\u00A0/g, " "); // non-breaking space

    if (!csvHasDataRows(content)) {
      setError(EMPTY_CSV_ERROR);
      setCsvFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Create UTF-8 CSV
    const utf8Blob = new Blob(
      [new TextEncoder().encode(content)],
      {
        type: "text/csv;charset=utf-8",
      }
    );

    const utf8File = new File(
      [utf8Blob],
      file.name,
      {
        type: "text/csv;charset=utf-8",
        lastModified: file.lastModified,
      }
    );

    setError(null);
    setCsvFile(utf8File);
  } catch (err) {
    console.error(err);

    setError(
      "Unable to process CSV. Please save the file as UTF-8 CSV and try again."
    );

    setCsvFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }
};

  const handleDeleteHeaderFile = () => {
    setCsvFile(null);
    setError(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
 

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
      {/* Upload CSV Section - New UI */}
      <Grid item xs={12}>
        <Typography sx={{ ...typography.h2, mb: 2, color: "#1D2330" }}>
          Upload CSV <Typography component="span" sx={{ color: "#d32f2f" }}>*</Typography>
        </Typography>
        
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            backgroundColor: "#FFFFFF",
            border: "1px dashed #DCDFE5",
            borderRadius: 1,
            textAlign: "center",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "#f3f4f67c",
              borderColor: ColorsConstants.primaryBackgroundColor,
            },
          }}
          onClick={handleUploadClick}
        >
          <input
            id="csv-file-upload"
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <UploadMain sx={{ mb:8 }} />
            <Typography sx={{ ...typography.bodyLarge, color: "#676F7E" }}>
              <span style={{color:ColorsConstants.primaryBackgroundColor, fontWeight: 600}}> Click to upload </span>or drag and drop
            </Typography>
            <Typography sx={{ ...typography.bodySmall, color: "#676F7E" }}>
              (Max. 10,000 rows per upload)
            </Typography>
          </Box>
        </Paper>

        {/* Sample file link */}
        <Box sx={{ mt: 2, textAlign:'start' }}>
          <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>
            Click{" "}
            <a
              href={sampleFileURL}
              download="sample.csv"
              style={{
                color: "#2563EB",
                textDecoration: "none",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              here
            </a>{" "}
            to download sample file
          </Typography>
        </Box>

        {/* Error Message */}
{error && (
  <Typography
    sx={{
      mt: 2,
      color: "#d32f2f",
      fontSize: "14px",
      fontWeight: 500,
    }}
  >
    {error}
  </Typography>
)}

        {/* Selected file display */}
        {csvFile && (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: 2,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <InsertDriveFileIcon sx={{ color: "#6B7280", fontSize: 20 }} />
                <Typography sx={{ ...typography.bodyLarge, color: "#1F2937", fontWeight: 600 }}>
                  {csvFile.name}
                </Typography>
                <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>
                  ({(csvFile.size / 1024).toFixed(2)} KB)
                </Typography>
              </Stack>
              <IconButton onClick={handleDeleteHeaderFile} size="small">
                <DeleteIcon sx={{ fontSize: 18, color: "#6B7280" }} />
              </IconButton>
            </Stack>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
});

export default UploadCSV;